import React from 'react';
import { Icon } from '../../../components/base/Icon';
import { getIconDefinition } from '../../../components/IconSelector';
import { Tooltip } from '../../../components/overlays/Tooltip';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useNavigation } from '../../../hooks/useNavigation';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import type {
	FieldConfiguration,
	ListBuilderCallbacks,
	TableRow,
} from '../../types';
import { getFieldDisplayProperties } from './displayHelpers';

interface IconDisplayProps {
	field: FieldConfiguration;
	value: any;
	data: TableRow;
	callbacks?: ListBuilderCallbacks;
}

// Size mappings for icons
const sizeMap = {
	xs: 12,
	sm: 14,
	md: 16,
	lg: 20,
};

export function IconDisplay({
	field,
	value,
	data,
	callbacks,
}: IconDisplayProps) {
	const navigation = useNavigation();
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { styleConfig, link } = field;

	// Get display value, mapped icon name, and color using value mapping
	const {
		displayValue,
		iconName: mappedIconName,
		colorValue,
	} = getFieldDisplayProperties(field, value, data);
	// Get icon name - prioritize mapped icon, then styleConfig icon, then displayValue if it's an icon name
	const iconName =
		mappedIconName ||
		styleConfig.iconName ||
		(typeof displayValue === 'string' ? displayValue : null);

	// Get tooltip text from styleConfig
	const tooltipText = styleConfig.tooltip;

	if (!iconName) {
		return null;
	}

	// Get icon definition
	const iconDefinition = getIconDefinition(iconName);
	if (!iconDefinition) {
		return null;
	}

	// Get color from value mapping or color rule with theme-aware resolution
	const resolvedText = colorValue?.text
		? resolveThemeColor(colorValue.text, isDark)
		: null;
	const resolvedBg = colorValue?.bg
		? resolveThemeColor(colorValue.bg, isDark)
		: null;
	const iconColor = resolvedText ?? resolvedBg ?? colors.values.textSecondary;

	// Get opacity from color rule
	const iconOpacity = colorValue?.opacity;

	// Get size
	const iconSize = sizeMap[styleConfig.size] || sizeMap.md;

	// Handle link click
	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (!link?.enabled || !data) {
			return;
		}

		switch (link.type) {
			case 'callback':
				if (link.callbackName && callbacks && callbacks[link.callbackName]) {
					callbacks[link.callbackName](data);
				}
				break;
			case 'object':
				// Handle object navigation if needed
				break;
			case 'custom':
				if (link.customUrl) {
					navigation.navigateTo(link.customUrl, {
						state: {
							returnPath: window.location.pathname + window.location.search,
						},
					});
				}
				break;
		}
	};

	// Check if this icon has linking/callback enabled
	const isClickable = link?.enabled;

	const iconElement = (
		<Icon
			icon={iconDefinition}
			size={iconSize}
			color={iconColor}
			style={iconOpacity != null ? { opacity: iconOpacity } : undefined}
		/>
	);

	const content = isClickable ? (
		<button
			onClick={handleClick}
			className="flex items-center justify-center p-1 hover:opacity-70 transition-opacity cursor-pointer"
			type="button"
		>
			{iconElement}
		</button>
	) : (
		<div className="flex items-center justify-center">{iconElement}</div>
	);

	if (tooltipText) {
		return <Tooltip content={tooltipText}>{content}</Tooltip>;
	}

	return content;
}
